import React, {useEffect, useMemo, useState} from 'react';
import {
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Divider,
    useMediaQuery,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {withAuthenticationRequired} from '@auth0/auth0-react';
import Loading from '../components/common/Loading';
import AppSnackbar from '../common/AppSnackbar';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useReservationAdminAPI} from '../service/useReservationAdminAPI';
import {parseISO} from 'date-fns';
import ReservationTile from '../components/management/ReservationTile';
import AppointmentTile from '../components/management/AppointmentTile';
import {formatDate} from "../common/DateUtil";
import PetsIcon from '@mui/icons-material/Pets';
import {useTenantConfig} from "../security/tenant-config-context";
import AddReservation from "../components/management/AddReservation";
import AddAppointment from "../components/management/AddAppointment";
import AnimalPhotoUploadModal from "../components/management/AnimalPhotoUploadModal";

const animalModel = {
    name: '',
    birthDate: '',
    breed: '',
    sex: '',
    color: '',
    chipNumber: '',
    spayedNeutered: false,
    ontvlooitEnOntwormt: false,
    kattenziekte: false,
    niesziekte: false,
    kattenleukemie: false,
    // DOG vaccinations (optional)
    dhp: false,
    leptospirose: false,
    kennelCough: false,
    rabies: false,
    // Dog medical extensions (optional)
    weightKg: null,
    sizeCategory: '',
    hasMedication: false,
    medicationNotes: '',
    allergies: '',
    femaleInHeat: null,
    lastHeatDate: '',
    medicalHistory: '',
    diet: '',
    behaviour: '',
    associationAnimals: '',
    contactOtherAnimals: false,
    // Dog behavior (optional)
    dogSocialization: 'UNKNOWN',
    reactiveOnLeash: null,
    resourceGuarding: null,
    separationAnxiety: null,
    houseTrained: null,
    biteHistory: null,
    biteHistoryNotes: '',
    muzzleRequired: null,
    okWithCats: null,
};

const InfoRow = ({label, content, stack = false}) => (
    <Box sx={{
        display: 'flex',
        flexDirection: stack ? 'column' : 'row',
        flexWrap: stack ? 'nowrap' : 'wrap',
        columnGap: stack ? 0 : 1,
        rowGap: stack ? 0.25 : 0.5,
        alignItems: stack ? 'flex-start' : 'baseline'
    }}>
        <Typography fontWeight={600} variant={stack ? 'caption' : undefined} sx={{opacity: stack ? 0.8 : 1}}>{label}:</Typography>
        <Typography component="span" sx={{mt: stack ? 0.25 : 0}}>{content ? (stack ? content : `${content}`) : (stack ? '-' : '-')}</Typography>
    </Box>
);

const BoolRow = ({label, value}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        {value ? (
            <CheckCircleIcon sx={{fontSize: 18, color: 'success.main'}}/>
        ) : (
            <CancelIcon sx={{fontSize: 18, color: 'error.main'}}/>
        )}
        <Typography>{label}</Typography>
    </Box>
);

const AnimalDetail = () => {
    const {t} = useTranslation();
    const {getPetDetail, uploadAnimalPhoto, getAnimalPhoto} = useReservationAdminAPI();
    const {uuid} = useParams();

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));

    const [currentAnimal, setCurrentAnimal] = useState(animalModel);
    const [animalPhotoUrl, setAnimalPhotoUrl] = useState(null);
    const [value, setValue] = useState('1');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [severity, setSeverity] = useState('error');
    const [isLoading, setIsLoading] = useState(true);
    const [openUploadModal, setOpenUploadModal] = useState(false);

    const [editReservationData, setEditReservationData] = useState(null);
    const [editAppointmentData, setEditAppointmentData] = useState(null);
    const isDog = (currentAnimal?.type || '').toUpperCase() === 'DOG';

    const {cfg} = useTenantConfig();
    const isHotelFlow = cfg?.businessTypes?.includes('HOTEL_BASED');

    function handleFetchError(reason) {
        setSnackbarMessage(reason);
        setSeverity('error');
        setOpenSnackbar(true);
    }

    function handleSuccess(message) {
        setSnackbarMessage(message);
        setSeverity('success');
        setOpenSnackbar(true);
    }

    const handlePhotoUpload = async (file) => {
        try {
            const result = await uploadAnimalPhoto(uuid, file);
            if (result && result.photoUrl) {
                setCurrentAnimal((prev) => ({ ...prev, photoUrl: result.photoUrl }));
                handleSuccess(t('photoSaved'));
            } else {
                refreshData();
                handleSuccess(t('photoSaved'));
            }
        } catch (error) {
            console.error('Upload failed', error);
            throw error;
        }
    };

    useEffect(() => {
        setIsLoading(true);
        getPetDetail(uuid)
            .then((val) => {
                if (val != null) setCurrentAnimal(val);
                else throw new Error('Geen katten gevonden');
            })
            .catch(() => handleFetchError(t('fetchCatError')))
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    useEffect(() => {
        let objectUrl = null;
        if (currentAnimal?.photoUrl) {
            getAnimalPhoto(currentAnimal.photoUrl)
                .then(url => {
                    objectUrl = url;
                    setAnimalPhotoUrl(url);
                })
                .catch(err => {
                    console.error('Failed to fetch animal photo', err);
                    setAnimalPhotoUrl(null);
                });
        } else {
            setAnimalPhotoUrl(null);
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [currentAnimal?.photoUrl]);

    const refreshData = () => {
        getPetDetail(uuid)
            .then((val) => {
                if (val != null) setCurrentAnimal(val);
            })
            .catch(() => handleFetchError(t('fetchCatError')));
    };

    const handleEditReservation = (reservation: any) => {
        setEditReservationData({
            ...reservation,
            person: currentAnimal.owner,
            animals: reservation.animals,
            animalsInReservationIds: reservation.animals.map((a: any) => a.id)
        });
    };

    const handleEditAppointment = (appointment: any) => {
        setEditAppointmentData({
            ...appointment,
            person: currentAnimal.owner,
            personId: currentAnimal.owner?.id,
            animals: [currentAnimal],
            animalsInReservationIds: appointment.animalsInReservationIds || (currentAnimal.id ? [currentAnimal.id] : [])
        });
    };

    const handleTabChange = (event, newValue) => setValue(newValue);

    const now = useMemo(() => new Date(), []);
    const upcomingReservation = (currentAnimal?.reservations ?? [])
        .filter((r) => parseISO(r.toDate) > now)
        .map((r, idx) => (
            <ReservationTile
                key={`up-${idx}`}
                reservation={r}
                onEdit={handleEditReservation}
                t={t}
            />
        ));

    const upcomingAppointments = (currentAnimal?.appointments ?? [])
        .filter((r) => parseISO(r.startAt) > now)
        .map((r, idx) => (
            <AppointmentTile
                key={`up-app-${idx}`}
                appointment={r}
                onEdit={handleEditAppointment}
                t={t}
            />
        ));
    const pastReservation = (currentAnimal?.reservations ?? [])
        .filter((r) => parseISO(r.toDate) < now)
        .map((r, idx) => (
            <ReservationTile
                key={`past-${idx}`}
                reservation={r}
                t={t}
            />
        ));

    const pastAppointments = (currentAnimal?.appointments ?? [])
        .filter((r) => parseISO(r.startAt) < now)
        .map((r, idx) => (
            <AppointmentTile
                key={`past-app-${idx}`}
                appointment={r}
                t={t}
            />
        ));

    // Build sections once per render (used by both Tabs and Accordions)
    const sections = [
        {
            key: '1',
            label: t('healthSection'),
            node: (
                <Box sx={{display: 'grid', gap: 1}} data-testid="health-section-content">
                    <InfoRow label={t('diet')} content={currentAnimal.diet} stack={isXs}/>
                    <InfoRow label={t('medicalHistory')} content={currentAnimal.medicalHistory} stack={isXs}/>
                    {isDog && (
                        <>
                            <BoolRow label={t('dog_medical_hasMedication')} value={!!currentAnimal?.hasMedication}/>
                            {currentAnimal?.hasMedication && (
                                <InfoRow label={t('dog_medical_medicationNotes')} content={currentAnimal?.medicationNotes || ''} stack={isXs}/>
                            )}
                            <InfoRow label={t('dog_medical_weightKg')} content={(currentAnimal?.weightKg ?? '')?.toString()} stack={isXs}/>
                            <InfoRow label={t('dog_medical_sizeCategory')} content={currentAnimal?.sizeCategory || ''} stack={isXs}/>
                            <InfoRow label={t('dog_medical_allergies')} content={currentAnimal?.allergies || ''} stack={isXs}/>
                            <BoolRow label={t('dog_medical_femaleInHeat')} value={!!currentAnimal?.femaleInHeat}/>
                            {currentAnimal?.femaleInHeat && (
                                <InfoRow label={t('dog_medical_lastHeatDate')} content={currentAnimal?.lastHeatDate ? formatDate(currentAnimal.lastHeatDate) : ''} stack={isXs}/>
                            )}
                        </>
                    )}
                </Box>
            ),
        },
        {
            key: '2',
            label: t('behaviorSection'),
            node: (
                <Box sx={{display: 'grid', gap: 1}}>
                    {!isDog && (
                        <>
                            <InfoRow label={t('behavior', {name: currentAnimal.name})} content={currentAnimal.behaviour} stack={isXs}/>
                            <InfoRow label={t('associationAnimals')} content={currentAnimal.associationAnimals} stack={isXs}/>
                            <BoolRow label={t('allowContact')} value={currentAnimal.contactOtherAnimals}/>
                        </>
                    )}
                    {isDog && (
                        <>
                            <InfoRow label={t('dog_behavior_socialization')} content={t(
                                (currentAnimal?.dogSocialization || 'UNKNOWN') === 'GROUP_OK' ? 'dog_socialization_group_ok' :
                                    (currentAnimal?.dogSocialization || 'UNKNOWN') === 'SMALL_GROUP_ONLY' ? 'dog_socialization_small_group_only' :
                                        (currentAnimal?.dogSocialization || 'UNKNOWN') === 'SOLO_ONLY' ? 'dog_socialization_solo_only' :
                                            'dog_socialization_unknown'
                            )} stack={isXs}/>
                            {currentAnimal?.biteHistory && (
                                <InfoRow label={t('dog_behavior_biteHistoryNotes')} content={currentAnimal?.biteHistoryNotes || ''} stack={isXs}/>
                            )}
                            <BoolRow label={t('dog_behavior_reactiveOnLeash')} value={!!currentAnimal?.reactiveOnLeash}/>
                            <BoolRow label={t('dog_behavior_resourceGuarding')} value={!!currentAnimal?.resourceGuarding}/>
                            <BoolRow label={t('dog_behavior_separationAnxiety')} value={!!currentAnimal?.separationAnxiety}/>
                            <BoolRow label={t('dog_behavior_houseTrained')} value={!!currentAnimal?.houseTrained}/>
                            <BoolRow label={t('dog_behavior_biteHistory')} value={!!currentAnimal?.biteHistory}/>
                            <BoolRow label={t('dog_behavior_muzzleRequired')} value={!!currentAnimal?.muzzleRequired}/>
                            <BoolRow label={t('dog_behavior_okWithCats')} value={!!currentAnimal?.okWithCats}/>
                        </>
                    )}
                </Box>
            ),
        },
        {
            key: '3',
            label: t('contactsSection'),
            node: (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" align="left" mb={1.5}>
                            {t('owner')}
                        </Typography>
                        <Box sx={{display: 'grid', gap: 1}}>
                            <InfoRow label={t('name')} content={`${currentAnimal.owner?.forename ?? ''} ${currentAnimal.owner?.name ?? ''}`} stack={isXs}/>
                            <InfoRow label={t('street')}
                                     content={`${currentAnimal.owner?.street ?? ''} ${currentAnimal.owner?.number ?? ''}${currentAnimal.owner?.box ? ' ' + currentAnimal.owner.box : ''}, ${currentAnimal.owner?.postalcode ?? ''} ${currentAnimal.owner?.municipality ?? ''}`}
                                     stack={isXs}/>
                            <InfoRow label={t('customersPage.phone')} content={currentAnimal.owner?.gsm} stack={isXs}/>
                            <InfoRow label={t('email')} content={currentAnimal.owner?.email} stack={isXs}/>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" align="left" mb={1.5}>
                            {t('emergencyContact')}
                        </Typography>
                        <Box sx={{display: 'grid', gap: 1}}>
                            <InfoRow label={t('name')} content={`${currentAnimal.emergencyContact?.forename ?? ''} ${currentAnimal.emergencyContact?.name ?? ''}`} stack={isXs}/>
                            <InfoRow label={t('street')}
                                     content={`${currentAnimal.emergencyContact?.street ?? ''} ${currentAnimal.emergencyContact?.number ?? ''}${currentAnimal.emergencyContact?.box ? ' ' + currentAnimal.emergencyContact.box : ''}, ${currentAnimal.emergencyContact?.postalcode ?? ''} ${currentAnimal.emergencyContact?.municipality ?? ''}`}
                                     stack={isXs}/>
                            <InfoRow label={t('customersPage.phone')} content={currentAnimal.emergencyContact?.gsm} stack={isXs}/>
                            <InfoRow label={t('email')} content={currentAnimal.emergencyContact?.email} stack={isXs}/>
                        </Box>
                    </Grid>
                </Grid>
            ),
        },
        {
            key: '4',
            label: t('veterinarian'),
            node: (
                <Box sx={{display: 'grid', gap: 1}}>
                    <InfoRow label={t('practice')} content={currentAnimal.veterinary?.practice} stack={isXs}/>
                    <InfoRow label={t('location')} content={currentAnimal.veterinary?.place} stack={isXs}/>
                    <InfoRow label={t('customersPage.phone')} content={currentAnimal.veterinary?.phoneNumber} stack={isXs}/>
                </Box>
            ),
        },
        ...(isHotelFlow
            ? [
                {
                    key: '5',
                    label: t('reservationsSection'),
                    node: (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="h6" align="left" mb={1.5}>
                                    {t('upcomingReservations')}
                                </Typography>
                                {upcomingReservation.length !== 0 ? (
                                    upcomingReservation
                                ) : (
                                    <Typography align="left">{t('noUpcomingReservations')}</Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="h6" align="left" mb={1.5}>
                                    {t('pastReservations')}
                                </Typography>
                                {pastReservation.length !== 0 ? (
                                    pastReservation
                                ) : (
                                    <Typography align="left">{t('noPastReservations')}</Typography>
                                )}
                            </Grid>
                        </Grid>
                    ),
                },
            ]
            : []),
        ...(!isHotelFlow
            ? [{
                key: '6',
                label: t('appointmentsSection'),
                node: (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6" align="left" mb={1.5}>
                                {t('upcomingAppointments')}
                            </Typography>
                            {upcomingAppointments.length !== 0 ? (
                                upcomingAppointments
                            ) : (
                                <Typography align="left">{t('noUpcomingAppointments')}</Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6" align="left" mb={1.5}>
                                {t('pastAppointments')}
                            </Typography>
                            {pastAppointments.length !== 0 ? (
                                pastAppointments
                            ) : (
                                <Typography align="left">{t('noPastAppointments')}</Typography>
                            )}
                        </Grid>
                    </Grid>
                ),
            }] : [])
    ];

    return (
        <>
            {isLoading && <Loading/>}
            {!isLoading && (
                <Grid container justifyContent="center" spacing={1} sx={{px: {xs: 1, sm: 2}, py: {xs: 1, sm: 2}}}>
                    {editReservationData && (
                        <AddReservation
                            initialData={editReservationData}
                            reservationId={editReservationData.uuid}
                            onSave={() => {
                                setEditReservationData(null);
                                refreshData();
                            }}
                            onClose={() => setEditReservationData(null)}
                        />
                    )}
                    {editAppointmentData && (
                        <AddAppointment
                            initialData={editAppointmentData}
                            appointmentId={editAppointmentData.uuid}
                            onSave={() => {
                                setEditAppointmentData(null);
                                refreshData();
                            }}
                            onClose={() => setEditAppointmentData(null)}
                        />
                    )}
                    <AnimalPhotoUploadModal
                        open={openUploadModal}
                        onClose={() => setOpenUploadModal(false)}
                        onUpload={handlePhotoUpload}
                        animalName={currentAnimal?.name}
                        t={t}
                    />
                    {/* Top section */}
                    <Grid item xs={12} sm={11} md={10} lg={8}>
                        <Card variant="outlined" sx={{mb: 1}}>
                            <CardHeader
                                title={
                                    <Typography color="secondary" variant={isXs ? 'h6' : 'h5'}>
                                        {t('basicInfo')}
                                    </Typography>
                                }
                            />
                            <Divider/>
                            <CardContent>
                                <Grid container spacing={2} alignItems="flex-start">
                                    {/* Animal picture placeholder */}
                                    <Grid item xs={12} sm={4} sx={{textAlign: {xs: 'center'}}}>
                                        <Box sx={{display: 'flex', justifyContent: {xs: 'center'}}}>
                                            <Box sx={{width: isXs ? '70%' : '100%'}}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        width: '100%',
                                                        pt: '100%', // 1:1 ratio
                                                        borderRadius: 2,
                                                        bgcolor: 'grey.100',
                                                        border: animalPhotoUrl ? '1px solid' : '2px dashed',
                                                        borderColor: animalPhotoUrl ? 'divider' : 'grey.400',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Box sx={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                        {animalPhotoUrl ? (
                                                            <Box
                                                                component="img"
                                                                src={animalPhotoUrl}
                                                                alt={currentAnimal.name}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                                onError={(e) => {
                                                                    const target = e.target;
                                                                    target.onerror = null;
                                                                    target.src = ''; // Force fallback
                                                                    setAnimalPhotoUrl(null);
                                                                }}
                                                            />
                                                        ) : (
                                                            <PetsIcon sx={{fontSize: isXs ? 56 : 72, color: 'grey.500'}}/>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ mt: 1, textAlign: 'center' }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => setOpenUploadModal(true)}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {animalPhotoUrl ? t('changePhoto') : t('uploadPhoto')}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Main info */}
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{display: 'grid', gap: 1}}>
                                            <InfoRow label={t('name')} content={currentAnimal.name}/>
                                            <InfoRow label={t('birthDate')} content={formatDate(currentAnimal.birthDate)}/>
                                            <InfoRow label={t('breed')} content={currentAnimal.breed}/>
                                            <InfoRow label={t('sex')}
                                                     content={currentAnimal.sex === 'Female' ? t("female") : currentAnimal.sex === 'Male' ? t('male') : t('unknown')}/>
                                            <InfoRow label={t('color')} content={currentAnimal.color}/>
                                            <InfoRow label={t('chipNumber')} content={currentAnimal.chipNumber}/>
                                            <InfoRow label={`${t('email')} ${t('owner').toLowerCase()}`} content={currentAnimal.owner?.email}/>
                                            <InfoRow label={`${t('phoneNumber')} ${t('owner').toLowerCase()}`} content={currentAnimal.owner?.gsm}/>
                                        </Box>
                                    </Grid>

                                    {/* Booleans */}
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{display: 'grid', gap: 1}}>
                                            <BoolRow label={t('neutered')} value={currentAnimal.spayedNeutered}/>
                                            <BoolRow label={t('dewormed')} value={currentAnimal.ontvlooitEnOntwormt}/>
                                            {!isDog && (
                                                <>
                                                    <BoolRow label={t('leukemia')} value={currentAnimal.kattenleukemie}/>
                                                    <BoolRow label={t('panleukopenia')} value={currentAnimal.kattenziekte}/>
                                                    <BoolRow label={t('coryza')} value={currentAnimal.niesziekte}/>
                                                    <BoolRow label={t('allowContact')} value={currentAnimal.contactOtherAnimals}/>
                                                </>
                                            )}
                                            {isDog && (
                                                <>
                                                    <BoolRow label={t('dog_vaccination_dhp')} value={currentAnimal.dhp}/>
                                                    <BoolRow label={t('dog_vaccination_lepto')} value={currentAnimal.leptospirose}/>
                                                    <BoolRow label={t('dog_vaccination_kennel_cough')} value={currentAnimal.kennelCough}/>
                                                    <BoolRow label={t('dog_vaccination_rabies_optional')} value={currentAnimal.rabies}/>
                                                </>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Details section: Accordions on mobile, Tabs on >= sm */}
                    <Grid item xs={12} sm={11} md={10} lg={8}>
                        {isXs ? (
                            <Card variant="outlined" sx={{mb: 1}} >
                                {sections.map((s, i) => (
                                    <Accordion key={s.key} defaultExpanded={i === 0} disableGutters>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                            <Typography variant="subtitle1">{s.label}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>{s.node}</AccordionDetails>
                                    </Accordion>
                                ))}
                            </Card>
                        ) : (
                            <Card variant="outlined" sx={{mb: 1}}>
                                <TabContext value={value}>
                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            bgcolor: 'background.paper',
                                            px: 1,
                                            pt: 1,
                                        }}
                                    >
                                        <TabList
                                            onChange={handleTabChange}
                                            variant="scrollable"
                                            scrollButtons
                                            allowScrollButtonsMobile
                                            aria-label="Pet detail tabs"
                                            data-testid="animal-detail-tabs"
                                        >
                                            {sections.map((section) => (
                                                <Tab key={section.key} label={section.label} value={section.key} data-testid={`tab-${section.key}`}/>
                                            ))}
                                        </TabList>
                                    </Box>

                                    <Divider/>

                                    {sections.map((section) => (
                                        <TabPanel key={section.key} value={section.key} sx={{px: {xs: 1, sm: 2}}}>
                                            {section.node}
                                        </TabPanel>
                                    ))}
                                </TabContext>
                            </Card>
                        )}
                    </Grid>

                    <AppSnackbar
                        openSnackbar={openSnackbar}
                        snackbarMessage={snackbarMessage}
                        severity={severity}
                        setOpenSnackbar={setOpenSnackbar}
                    />
                </Grid>
            )}
        </>
    );
};

export default withAuthenticationRequired(AnimalDetail, {
    onRedirecting: () => <Loading/>,
    returnTo: '/beheer/login',
});
